<template>
  <div>
    <span
      class="af-text-button af-text-button--expanded has-text-blue has-text-weight-medium"
      @click="openModal"
    >
      {{ buttonText }}
    </span>

    <b-modal v-model="isModalOpen" :can-cancel="false">
      <div class="modal-card">
        <p class="has-text-weight-bold is-size-4 mb-4">
          {{ $t("af:offer_details.title") }}
        </p>
        <div class="selected-offer-card selected-offer-card--desktop mb-5">
          <PartnerLogo :offerId="offer.data.id" class="mb-4" />

          <p class="has-text-weight-bold">
            {{ offer.data.name }}
          </p>
        </div>

        <div class="modal-card__content">
          <div>
            <div class="modal-navigator modal-navigator--desktop">
              <div
                class="navigation-entry"
                :class="{ 'navigation-entry--active': activeTab === 0 }"
                @click="scrollTo(0)"
              >
                {{ $t("af:offer_details.navigator.costs") }}
              </div>
              <div
                class="navigation-entry"
                :class="{ 'navigation-entry--active': activeTab === 1 }"
                @click="scrollTo(1)"
              >
                {{ $t("af:offer_details.navigator.details") }}
              </div>
              <div
                class="navigation-entry"
                :class="{ 'navigation-entry--active': activeTab === 2 }"
                @click="scrollTo(2)"
              >
                {{ $t("af:offer_details.navigator.terms_of_sale") }}
              </div>
              <div
                class="navigation-entry"
                :class="{ 'navigation-entry--active': activeTab === 3 }"
                @click="scrollTo(3)"
              >
                {{ $t("af:offer_details.navigator.prepayment") }}
              </div>
              <div
                class="navigation-entry"
                :class="{ 'navigation-entry--active': activeTab === 4 }"
                @click="scrollTo(4)"
              >
                {{ $t("af:offer_details.navigator.legal_details") }}
              </div>
              <div
                class="navigation-entry"
                :class="{ 'navigation-entry--active': activeTab === 5 }"
                @click="scrollTo(5)"
              >
                {{ $t("af:offer_details.navigator.representative_example") }}
              </div>
            </div>
          </div>

          <div>
            <div class="selected-offer-card selected-offer-card--mobile mb-4">
              <PartnerLogo :offerId="offer.data.id" class="mb-4" />

              <p class="has-text-weight-bold">
                {{ offer.data.name }}
              </p>
            </div>

            <div class="modal-navigator modal-navigator--mobile mb-5">
              <div
                class="navigation-entry"
                :class="{ 'navigation-entry--active': activeTab === 0 }"
                @click="scrollTo(0)"
              >
                {{ $t("af:offer_details.navigator.costs") }}
              </div>
              <div
                class="navigation-entry"
                :class="{ 'navigation-entry--active': activeTab === 1 }"
                @click="scrollTo(1)"
              >
                {{ $t("af:offer_details.navigator.details") }}
              </div>
              <div
                class="navigation-entry"
                :class="{ 'navigation-entry--active': activeTab === 2 }"
                @click="scrollTo(2)"
              >
                {{ $t("af:offer_details.navigator.terms_of_sale") }}
              </div>
              <div
                class="navigation-entry"
                :class="{ 'navigation-entry--active': activeTab === 3 }"
                @click="scrollTo(3)"
              >
                {{ $t("af:offer_details.navigator.prepayment") }}
              </div>
              <div
                class="navigation-entry"
                :class="{ 'navigation-entry--active': activeTab === 4 }"
                @click="scrollTo(4)"
              >
                {{ $t("af:offer_details.navigator.legal_details") }}
              </div>
              <div
                class="navigation-entry"
                :class="{ 'navigation-entry--active': activeTab === 5 }"
                @click="scrollTo(5)"
              >
                {{ $t("af:offer_details.navigator.representative_example") }}
              </div>
            </div>

            <div v-observe-visibility="visibilityChanged" id="0">
              <p class="has-text-weight-bold mb-2">
                {{ $t("af:offer_details.details.title.costs") }}
              </p>
              <p class="is-size-7 has-text-grey-icon">
                {{ $t("af:offer_details.details.subtitle.costs") }}
              </p>

              <div class="offer-details__table">
                <div class="detail-row">
                  <div class="detail-row__label has-text-grey-icon">
                    {{ $t("af:offer_details.details.costs.total_cost") }}
                  </div>
                  <div class="detail-row__value has-text-weight-medium">
                    {{ monify(offer.calculation.totalCost) }}
                    {{ $t("af:offer_details.details.costs.total_cost_unit") }}
                  </div>
                </div>

                <div class="detail-row">
                  <div class="detail-row__label has-text-grey-icon">
                    {{ $t("af:offer_details.details.costs.start_fee") }}
                  </div>
                  <div class="detail-row__value has-text-weight-medium">
                    {{ offer.data.starterFee }}
                  </div>
                </div>

                <div class="detail-row">
                  <div class="detail-row__label has-text-grey-icon">
                    {{ $t("af:offer_details.details.costs.interest_period") }}
                  </div>
                  <div class="detail-row__value has-text-weight-medium">
                    {{ offer.data.interestPeriod }}
                  </div>
                </div>

                <div class="detail-row">
                  <div class="detail-row__label has-text-grey-icon">
                    {{
                      $t(
                        "af:offer_details.details.costs.interest_rate_spread_indicator"
                      )
                    }}
                  </div>
                  <div class="detail-row__value has-text-weight-medium">
                    {{ offer.data.interestRateSpreadIndicator }}
                  </div>
                </div>

                <div class="detail-row">
                  <div class="detail-row__label has-text-grey-icon">
                    {{ $t("af:offer_details.details.costs.interest_type") }}
                  </div>
                  <div class="detail-row__value has-text-weight-medium">
                    {{ offer.data.interestType }}
                  </div>
                </div>

                <div class="detail-row">
                  <div class="detail-row__label has-text-grey-icon">
                    {{ $t("af:offer_details.details.costs.other_fee") }}
                  </div>
                  <div class="detail-row__value has-text-weight-medium">
                    {{ offer.data.otherFee }}
                  </div>
                </div>
              </div>
            </div>

            <div v-observe-visibility="visibilityChanged" id="1">
              <br />
              <p class="has-text-weight-bold">
                {{ $t("af:offer_details.details.title.details") }}
              </p>
              <p>
                {{ offer.data.description }}
              </p>
            </div>

            <div v-observe-visibility="visibilityChanged" id="2">
              <br />
              <p class="has-text-weight-bold">
                {{ $t("af:offer_details.details.title.terms_of_sale") }}
              </p>
              <p>{{ offer.data.saleDetails }}</p>
            </div>

            <div v-observe-visibility="visibilityChanged" id="3">
              <br />
              <p class="has-text-weight-bold">
                {{ $t("af:offer_details.details.title.prepayment") }}
              </p>
              <p>{{ offer.data.prepaymentDetails }}</p>
            </div>

            <div v-observe-visibility="visibilityChanged" id="4">
              <br />
              <p class="has-text-weight-bold">
                {{ $t("af:offer_details.details.title.legal_details") }}
              </p>
              <p>{{ offer.data.legalDetails }}</p>
            </div>

            <div v-observe-visibility="visibilityChanged" id="5">
              <br />
              <p class="has-text-weight-bold">
                {{
                  $t("af:offer_details.details.title.representative_example")
                }}
              </p>
              <p>{{ offer.data.representativeExample }}</p>
            </div>
          </div>
        </div>

        <div class="close-btn cursor-pointer" @click="cancelModal">
          <b-icon icon-pack="fas" icon="times" type="is-grey-icon" />
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { monify } from "../../utils/util";

export default {
  name: "OfferDetailsModal",
  components: {
    PartnerLogo: () => import("@/components/PartnerLogo"),
  },
  props: {
    offer: {
      type: Object,
      required: true,
    },
    buttonText: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      monify,
      isModalOpen: false,
      activeTab: 0,
      tabVisibility: {},
      previousScrollTop: 0,
      scrollDirection: "up",
      scrollListener: null,
      scrollElement: null,
      stickyObserver: null,
    };
  },
  watch: {
    tabVisibility: {
      deep: true,
      handler: function () {
        const keys = Object.keys(this.tabVisibility);

        for (let i = 0; i < keys.length; i++) {
          if (this.tabVisibility[keys[i]]) {
            this.activeTab = i;

            if (this.scrollDirection === "up") {
              break;
            }
          }
        }
      },
    },
  },
  beforeDestroy() {
    this.cleanup();
  },
  methods: {
    openModal() {
      this.isModalOpen = true;

      this.$nextTick(() => {
        const element = document.querySelector(".modal-card__content");

        this.scrollListener = () => {
          const st = element.scrollTop;
          this.scrollDirection = st > this.previousScrollTop ? "down" : "up";
          this.previousScrollTop = st <= 0 ? 0 : st;
        };

        element.addEventListener("scroll", this.scrollListener, false);
        this.scrollElement = element;

        const el = document.querySelector(".modal-navigator--mobile");
        const observer = new IntersectionObserver(
          ([e]) =>
            e.target.classList.toggle("is-pinned", e.intersectionRatio < 1),
          { threshold: [1] }
        );

        observer.observe(el);
        this.stickyObserver = observer;
      });
    },
    cancelModal() {
      this.isModalOpen = false;
      this.cleanup();
    },
    visibilityChanged(isVisible, entry) {
      const elementIndex = parseInt(entry.target.id);

      this.$set(this.tabVisibility, elementIndex, isVisible);
    },
    scrollTo(id) {
      document.getElementById(id).scrollIntoView({
        behavior: "smooth",
      });
      this.activeTab = id;
    },
    cleanup() {
      this.activeTab = 0;

      if (this.scrollListener && this.scrollElement) {
        this.scrollElement.removeEventListener("scroll", this.scrollListener);
      }

      if (this.stickyObserver) {
        this.stickyObserver.disconnect();
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/colors.scss";

.is-pinned {
  box-shadow: 0 8px 6px -6px rgba(0, 0, 0, 0.16);
}

.modal-card {
  background: white;
  border: 1px solid $grey-lightest;
  border-radius: 24px;
  position: relative;
  max-height: 79vh;
  width: 100%;
  padding: 1rem;

  &__content {
    overflow-y: auto;
  }

  .selected-offer-card {
    background: $grey-2;
    border-radius: 6px;
    padding: 1rem;

    &--desktop {
      display: none;
    }
  }

  .modal-navigator {
    display: flex;

    .navigation-entry {
      cursor: pointer;
      color: $grey-icon;
      flex-shrink: 0;

      &:not(:last-child) {
        margin-right: 1rem;
      }
    }

    &--mobile {
      background: white;
      position: sticky;
      top: -1px;
      flex-direction: row;
      transition: box-shadow 0.2s;
      overflow-x: scroll;

      &::-webkit-scrollbar {
        display: none;
      }

      .navigation-entry {
        padding-bottom: 4px;

        &--active {
          color: $blue;
          position: relative;

          &:before {
            content: "";
            display: block;
            position: absolute;
            bottom: 0;
            width: 100%;
            height: 2px;
            background: $blue;
          }
        }
      }
    }

    &--desktop {
      display: none;
      position: absolute;
      flex-direction: column;

      .navigation-entry {
        margin-bottom: 8px;

        &--active {
          color: $blue;
          border-left: 4px solid $blue;
          padding-left: 4px;
        }
      }
    }
  }

  .close-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    height: 32px;
    width: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

.offer-details__table {
  width: 100%;

  .detail-row {
    padding: 10px 0;
    display: flex;
    flex-wrap: nowrap;
    justify-content: space-between;

    &:not(:last-child) {
      border-bottom: 1px solid $grey-1;
    }
  }
}

@media only screen and (min-width: 769px) {
  .modal-card {
    padding: 48px;
  }

  .modal-card__content {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: 1rem;
    overflow-y: auto;

    div:nth-child(2) {
      grid-column-end: span 2;
    }
  }

  .modal-navigator {
    &--mobile {
      display: none !important;
    }

    &--desktop {
      display: flex !important;
    }
  }

  .selected-offer-card {
    &--mobile {
      display: none;
    }

    &--desktop {
      display: block !important;
    }
  }

  .offer-details__table {
    max-width: 350px;
  }
}
</style>
